import React, { useState, useEffect, useRef } from 'react';
import api from '../utils/api';

function SystemBackup() {
  const [backups, setBackups] = useState([]);
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [restoring, setRestoring] = useState(false);
  const [toggling, setToggling] = useState(false);
  const [message, setMessage] = useState('');
  const [selectedFile, setSelectedFile] = useState(null);
  const fileInputRef = useRef(null);

  useEffect(() => {
    fetchBackups();
    fetchStatus();
  }, []);

  const fetchBackups = async () => {
    setLoading(true);
    try {
      const response = await api.get('/admin/backups');
      setBackups(response.data.backups || []);
    } catch (err) {
      setMessage('Failed to load backups.');
    } finally {
      setLoading(false);
    }
  };

  const fetchStatus = async () => {
    try {
      const response = await api.get('/admin/backups/status');
      setStatus(response.data);
    } catch (err) {
      console.error('Failed to fetch backup status:', err);
    }
  };

  const toggleAutomatedBackup = async () => {
    if (!status) return;
    setToggling(true);
    setMessage('');
    try {
      const newEnabled = status.status !== 'Enabled';
      const response = await api.post('/admin/backups/settings', { automated_backup_enabled: newEnabled });
      setMessage(response.data.message);
      await fetchStatus();
    } catch (err) {
      setMessage(err.response?.data?.message || 'Failed to update automated backup setting.');
    } finally {
      setToggling(false);
    }
  };

  const createBackup = async () => {
    setCreating(true);
    setMessage('');
    try {
      const response = await api.post('/admin/backups');
      setMessage(response.data.message);
      await fetchBackups();
      await fetchStatus();
    } catch (err) {
      setMessage(err.response?.data?.message || 'Failed to create backup.');
    } finally {
      setCreating(false);
    }
  };

  const restoreBackup = async () => {
    if (!selectedFile) {
      setMessage('Please select a backup ZIP file to restore.');
      return;
    }

    if (!window.confirm('This will overwrite the current database and uploads. A safety backup will be created first. Continue?')) {
      return;
    }

    setRestoring(true);
    setMessage('');
    try {
      const formData = new FormData();
      formData.append('backupFile', selectedFile);

      const response = await api.post('/admin/backups/restore', formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });
      setMessage(response.data.message);
      setSelectedFile(null);
      if (fileInputRef.current) fileInputRef.current.value = '';
      await fetchBackups();
      await fetchStatus();
    } catch (err) {
      setMessage(err.response?.data?.message || 'Failed to restore backup.');
    } finally {
      setRestoring(false);
    }
  };

  const downloadFile = (backupName, fileName) => {
    window.open(`${api.defaults.baseURL}/admin/backups/${encodeURIComponent(backupName)}/download/${encodeURIComponent(fileName)}`, '_blank');
  };

  const downloadZip = (backupName) => {
    window.open(`${api.defaults.baseURL}/admin/backups/${encodeURIComponent(backupName)}/download-zip`, '_blank');
  };

  const deleteBackup = async (backupName) => {
    if (!window.confirm(`Are you sure you want to delete backup "${backupName}"? This action cannot be undone.`)) {
      return;
    }

    // Debug log to see exact URL being requested
    const apiUrl = api.defaults.baseURL;
    const encodedName = encodeURIComponent(backupName);
    const fullUrl = `/admin/backups/${encodedName}`;
    console.log('DEBUG DELETE:', {
      baseURL: apiUrl,
      requestPath: fullUrl,
      fullUrl: `${apiUrl}${fullUrl}`
    });

    try {
      const response = await api.delete(fullUrl);
      setMessage(response.data.message || 'Backup deleted successfully.');
      await fetchBackups();
    } catch (err) {
      console.error('Delete backup error details:', {
        message: err.message,
        status: err.response?.status,
        data: err.response?.data,
        config: err.config
      });
      setMessage(err.response?.data?.message || 'Failed to delete backup.');
    }
  };

  return (
    <div className="system-backup-container" style={{ padding: '24px', maxWidth: '1200px', margin: '0 auto' }}>
      <div className="page-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px', flexWrap: 'wrap', gap: '12px' }}>
        <div>
          <h1 style={{ margin: '0 0 4px 0', fontSize: '28px', fontWeight: '700' }}>System Backup</h1>
          <p style={{ margin: 0, color: '#6b7280' }}>Back up the MySQL database and uploaded files</p>
        </div>
        <button
          className="btn btn-primary"
          onClick={createBackup}
          disabled={creating}
          style={{
            backgroundColor: '#e63740',
            borderColor: '#e63740',
            color: '#fff',
            padding: '10px 20px',
            borderRadius: '6px',
            fontWeight: '600',
            cursor: creating ? 'not-allowed' : 'pointer',
            opacity: creating ? 0.7 : 1,
            display: 'flex',
            alignItems: 'center',
            gap: '8px'
          }}
        >
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
            <polyline points="17 8 12 3 7 8" />
            <line x1="12" y1="3" x2="12" y2="15" />
          </svg>
          {creating ? 'Creating...' : 'Create Backup Now'}
        </button>
      </div>

      {message && (
        <div className={`alert ${message.includes('success') || message.includes('successfully') ? 'alert-success' : 'alert-error'}`} style={{ marginBottom: '16px' }}>
          {message}
        </div>
      )}

      {/* Automated Backup Status */}
      {status && (
        <div className="card" style={{ padding: '24px', marginBottom: '24px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '12px' }}>
            <div>
              <h3 style={{ margin: '0 0 8px 0', fontSize: '16px', fontWeight: '700', color: '#374151' }}>AUTOMATED BACKUP</h3>
              <p style={{ margin: '0 0 12px 0', color: '#6b7280', fontSize: '14px' }}>
                Scheduled {status.schedule_readable} ({status.schedule})
              </p>
              <p style={{ margin: 0, color: '#374151', fontSize: '14px' }}>
                <strong>Last backup:</strong> {status.last_backup_formatted}
              </p>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <div style={{
                padding: '6px 14px',
                borderRadius: '20px',
                background: status.status === 'Enabled' ? '#d1fae5' : '#fee2e2',
                color: status.status === 'Enabled' ? '#059669' : '#dc2626',
                fontSize: '13px',
                fontWeight: '600'
              }}>
                {status.status}
              </div>
              <button
                onClick={toggleAutomatedBackup}
                disabled={toggling}
                style={{
                  padding: '6px 14px',
                  borderRadius: '20px',
                  border: '1px solid #d1d5db',
                  background: '#fff',
                  color: '#374151',
                  fontSize: '13px',
                  fontWeight: '600',
                  cursor: toggling ? 'not-allowed' : 'pointer',
                  opacity: toggling ? 0.7 : 1,
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px'
                }}
              >
                {status.status === 'Enabled' ? 'Disable' : 'Enable'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Import / Restore */}
      <div className="card" style={{ padding: '24px', marginBottom: '24px' }}>
        <h3 style={{ margin: '0 0 8px 0', fontSize: '16px', fontWeight: '700', color: '#374151' }}>IMPORT / RESTORE</h3>
        <p style={{ margin: '0 0 16px 0', color: '#6b7280', fontSize: '14px' }}>
          Upload a backup .zip to restore the database and uploaded files. A safety backup is created automatically before restoring.
        </p>
        <div style={{ display: 'flex', alignItems: 'center', gap: '16px', flexWrap: 'wrap' }}>
          <input
            type="file"
            accept=".zip"
            ref={fileInputRef}
            onChange={(e) => setSelectedFile(e.target.files[0])}
            style={{ padding: '8px', border: '1px solid #d1d5db', borderRadius: '6px' }}
          />
          <button
            className="btn btn-primary"
            onClick={restoreBackup}
            disabled={restoring || !selectedFile}
            style={{
              backgroundColor: '#e63740',
              borderColor: '#e63740',
              color: '#fff',
              padding: '10px 20px',
              borderRadius: '6px',
              fontWeight: '600',
              cursor: restoring || !selectedFile ? 'not-allowed' : 'pointer',
              opacity: restoring || !selectedFile ? 0.7 : 1,
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
              <polyline points="17 8 12 3 7 8" />
              <line x1="12" y1="3" x2="12" y2="15" />
            </svg>
            {restoring ? 'Restoring...' : 'Restore from ZIP'}
          </button>
        </div>
      </div>

      {/* Backups Table */}
      <div className="card" style={{ padding: '0', overflow: 'hidden' }}>
        {loading ? (
          <div className="loading" style={{ padding: '40px' }}>Loading backups...</div>
        ) : backups.length === 0 ? (
          <div className="empty-state" style={{ padding: '40px' }}>No backups found. Click "Create Backup Now" to create one.</div>
        ) : (
          <table className="table" style={{ margin: 0, width: '100%' }}>
            <thead>
              <tr style={{ backgroundColor: '#f9fafb' }}>
                <th style={{ padding: '16px 24px', textAlign: 'left', fontSize: '14px', fontWeight: '600', color: '#374151' }}>#</th>
                <th style={{ padding: '16px 24px', textAlign: 'left', fontSize: '14px', fontWeight: '600', color: '#374151' }}>Backup Name</th>
                <th style={{ padding: '16px 24px', textAlign: 'left', fontSize: '14px', fontWeight: '600', color: '#374151' }}>Created</th>
                <th style={{ padding: '16px 24px', textAlign: 'left', fontSize: '14px', fontWeight: '600', color: '#374151' }}>Files</th>
                <th style={{ padding: '16px 24px', textAlign: 'right', fontSize: '14px', fontWeight: '600', color: '#374151' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {backups.map((backup, index) => (
                <tr key={backup.name} style={{ borderTop: '1px solid #e5e7eb' }}>
                  <td style={{ padding: '16px 24px', color: '#6b7280' }}>{index + 1}</td>
                  <td style={{ padding: '16px 24px', fontWeight: '500', color: '#111827' }}>
                    <span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#9ca3af" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                        <polyline points="14 2 14 8 20 8" />
                      </svg>
                      {backup.name}
                    </span>
                  </td>
                  <td style={{ padding: '16px 24px', color: '#6b7280', fontSize: '14px' }}>{backup.created_at_formatted}</td>
                  <td style={{ padding: '16px 24px', color: '#6b7280', fontSize: '14px' }}>{backup.files.join(', ')}</td>
                  <td style={{ padding: '16px 24px', textAlign: 'right' }}>
                    <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end', flexWrap: 'wrap' }}>
                      {backup.files.map((file) => (
                        <button
                          key={file}
                          className="btn btn-sm"
                          onClick={() => downloadFile(backup.name, file)}
                          style={{
                            padding: '6px 12px',
                            border: '1px solid #d1d5db',
                            borderRadius: '6px',
                            background: '#fff',
                            color: '#374151',
                            cursor: 'pointer',
                            fontSize: '13px',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '4px'
                          }}
                        >
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                            <polyline points="7 10 12 15 17 10" />
                            <line x1="12" y1="15" x2="12" y2="3" />
                          </svg>
                          {file}
                        </button>
                      ))}
                      <button
                        className="btn btn-sm btn-primary"
                        onClick={() => downloadZip(backup.name)}
                        style={{
                          padding: '6px 12px',
                          borderRadius: '6px',
                          backgroundColor: '#e63740',
                          borderColor: '#e63740',
                          color: '#fff',
                          cursor: 'pointer',
                          fontSize: '13px',
                          fontWeight: '600',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '4px'
                        }}
                      >
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                          <polyline points="7 10 12 15 17 10" />
                          <line x1="12" y1="15" x2="12" y2="3" />
                        </svg>
                        ZIP
                      </button>
                      <button
                        onClick={() => deleteBackup(backup.name)}
                        style={{
                          padding: '6px 12px',
                          borderRadius: '6px',
                          backgroundColor: '#ef4444',
                          borderColor: '#ef4444',
                          color: '#fff',
                          cursor: 'pointer',
                          fontSize: '13px',
                          fontWeight: '600',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '4px'
                        }}
                      >
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M3 6h18" />
                          <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
                        </svg>
                        DELETE
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

export default SystemBackup;
